import React, { useState, useContext, useRef, useEffect, useMemo } from 'react';
import { AuthContext } from '../../context/AuthContext';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import userIcon from '../../assets/user.png';
import botIcon from '../../assets/robot.png';
import { LuClipboardCopy, LuCheck } from 'react-icons/lu';
import { FaFileAlt } from 'react-icons/fa';
import systemIcon from '../../assets/system.png';
import FilePreviewModal from '../../components/FilePreviewModal';
import styles from './Chat.module.css';
import { MdClose, MdDeleteOutline, MdWavingHand, MdAttachFile, MdStop } from 'react-icons/md';
import { IoSend } from 'react-icons/io5';
import axiosInstance from '../../api/axiosInstance';

const PAGE_SIZE = 2;
const maxTurns = 5;

/**
 * Collapse multiple "📁 Related Files:" sections into a single one
 * and deduplicate file lines.
 */
const normalizeRelatedFilesSections = (text) => {
  if (!text) return text;

  // Split by newlines but keep the structure
  const lines = text.split('\n');
  const contentLines = [];
  const fileLines = [];
  const seenFiles = new Set();

  let captureFiles = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Detected a "Related Files" header?
    if (line.match(/📁\s*\**Related Files:?\**/i)) {
      captureFiles = true;
      continue; // Skip the header itself
    }

    // If looks like a file line (bullet or link)
    if (line.includes('View File') || (captureFiles && line.startsWith('•'))) {
      // Normalize key for dedup: strip bullet and whitespace
      const key = line.replace(/^[•\-\*]\s*/, '').trim();

      if (!seenFiles.has(key)) {
        seenFiles.add(key);
        fileLines.push(line);
      }
    } else {
      // Normal content text
      if (line.length > 0) contentLines.push(lines[i]);
    }
  }

  const mainText = contentLines.join('\n');

  if (fileLines.length === 0) return mainText;

  // Use double newlines to ensure Markdown renders them as separate blocks
  return mainText + `\n\n📁 **Related Files:**\n\n` + fileLines.join('\n\n');
};



/**
 * Extract related file info from assistant reply text.
 * Very forgiving: just looks for lines mentioning "View File" and an http(s) URL.
 */
const extractFileLinks = (text) => {
  if (!text) return [];

  const files = [];
  const lines = text.split('\n');

  for (let rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Only care about lines that mention "View File"
    if (!line.toLowerCase().includes('view file')) continue;

    // 1) Grab the first http(s) URL on this line (the file link)
    const urlMatch = line.match(/https?:\/\/[^\s)]+/i);
    if (!urlMatch) {
      // No URL on this line -> skip (we don't want broken links in modal)
      continue;
    }
    const url = urlMatch[0].trim();

    // 2) File name = text before first "(" (without bullet / icon)
    const firstParenIdx = line.indexOf('(');
    let namePart = firstParenIdx !== -1 ? line.slice(0, firstParenIdx) : line;
    namePart = namePart.replace(/^[-•\u2022]\s*/, '').trim(); // remove bullet
    const name = namePart.replace(/^📁\s*/, '').trim(); // remove leading folder icon if any

    // 3) Info inside parentheses for metadata (optional)
    let uploadedAt = '';
    let lastModified = '';
    let folder = '';

    const parenMatch = line.match(/\(([^)]*)\)/);
    if (parenMatch && parenMatch[1]) {
      const info = parenMatch[1];

      const uploadedMatch = info.match(
        /Uploaded:\s*(.*?)(?:,\s*Last Modified:|,\s*Folder:|\s*$)/i
      );
      if (uploadedMatch) uploadedAt = uploadedMatch[1].trim();

      const lastMatch = info.match(/Last Modified:\s*(.*?)(?:,\s*Folder:|\s*$)/i);
      if (lastMatch) lastModified = lastMatch[1].trim();

      const folderMatch = info.match(/Folder:\s*([^)]+)$/i);
      if (folderMatch) folder = folderMatch[1].trim();
    }

    if (!name || !url) continue;

    files.push({
      name,
      uploadedAt,
      lastModified,
      folder,
      url, // real http(s) link used by chat
    });
  }

  return files;
};

const ChatBox = () => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const [previewFiles, setPreviewFiles] = useState([]);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showFileButton, setShowFileButton] = useState(false); // start hidden
  const [countdown, setCountdown] = useState(5);

  const { authData, currentGroup } = useContext(AuthContext);
  const messagesEndRef = useRef(null);
  const initializedRef = useRef(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc');
  const [copiedIndex, setCopiedIndex] = useState(null);

  // 📎 Session Files (Ephemeral Context)
  const [sessionFiles, setSessionFiles] = useState([]); // [{ name, text }]
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null); // Ref to store the controller

  const localStorageKey = `chatMessages_${currentGroup?._id}`;

  // ---- Init from localStorage ----
  useEffect(() => {
    if (!initializedRef.current) {
      const saved = localStorage.getItem(localStorageKey);
      if (saved) {
        try {
          const { messages, timestamp } = JSON.parse(saved);
          const isExpired = Date.now() - timestamp > 24 * 60 * 60 * 1000; // 24 hours

          if (!isExpired && messages) {
            setMessages(messages);
          } else {
            localStorage.removeItem(localStorageKey);
            setMessages([
              {
                role: 'assistant',
                content: 'Hello, I am Your Departmental AI Chatbot. How can I help you today?',
                timestamp: new Date().toISOString(),
              },
            ]);
          }
        } catch (e) {
          console.error('Failed to parse localStorage messages:', e);
        }
      } else {
        setMessages([
          {
            role: 'assistant',
            content: 'Hello, I am Your Departmental AI Chatbot. How can I help you today?',
            timestamp: new Date().toISOString(),
          },
        ]);
      }
      initializedRef.current = true;
    }
  }, [localStorageKey]);

  // ---- Persist last few turns ----
  useEffect(() => {
    if (initializedRef.current) {
      const persistedMessages = messages
        .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
        .slice(-maxTurns * 2);

      localStorage.setItem(
        localStorageKey,
        JSON.stringify({
          messages: persistedMessages,
          timestamp: Date.now(),
        })
      );
    }
  }, [messages, localStorageKey]);

  // ---- Scroll handling ----
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const chatContainer = messagesEndRef.current?.parentElement;
    chatContainer?.addEventListener('scroll', handleScroll);
    return () => chatContainer?.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    const container = messagesEndRef.current?.parentElement;
    if (container) {
      const isBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight - 50;
      setIsAtBottom(isBottom);
    }
  };

  useEffect(() => {
    if (messages.length > 1 && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [messages, hasLoaded]);

  // ---- Timer for bottom "View Related Files" banner ----
  useEffect(() => {
    let interval;
    if (showFileButton && previewFiles.length > 0) {
      setCountdown(90);
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowFileButton(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [previewFiles, showFileButton]);

  const getIcon = (role) => {
    if (role === 'user') return userIcon;
    if (role === 'assistant') return botIcon;
    return systemIcon;
  };

  const getSenderName = (role) => {
    if (role === 'user') return authData?.name || 'You';
    if (role === 'assistant') return 'AI BOT';
    return 'System';
  };

  const extractExternalLinks = (text) => {
    const urlRegex = /https?:\/\/[^\s]+/g;
    return text?.match(urlRegex) || [];
  };

  // ---- Shared function: send any question (user or "show related files") ----
  const sendQuestion = async (
    questionText,
    overridePage = 1,
    overrideSort = sortOrder
  ) => {
    if (!questionText.trim()) return;

    setLoading(true);
    setPreviewFiles([]);
    setShowFileButton(false); // hide banner until we know there are files
    setPage(overridePage);

    const chatHistory = messages
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
      .slice(-maxTurns * 2)
      .map(({ role, content }) => ({ role, content }));

    const extractedLinks = extractExternalLinks(questionText);

    const userMsg = {
      role: 'user',
      content: questionText,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Create new abort controller
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const requestBody = JSON.stringify({
        question: questionText,
        groupId: currentGroup?._id,
        history: chatHistory, // AI sees recent history
        page: overridePage,
        pageSize: PAGE_SIZE,
        sortOrder: overrideSort,
        externalLinks: extractedLinks,
        stream: true,
      });

      const API_BASE = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_BASE}/chat/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: requestBody,
        signal: controller.signal, // Attach signal
      });

      if (!response.ok || !response.body) {
        throw new Error('Network response was not ok or no response body');
      }

      const assistantMsg = {
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let done = false;
      let accumulated = '';

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          accumulated += chunk;

          setMessages((prev) => {
            const newMessages = [...prev];
            const lastIndex = newMessages.length - 1;
            newMessages[lastIndex] = {
              ...newMessages[lastIndex],
              content: accumulated,
            };
            return newMessages;
          });
        }
      }

      // ---- After assistant reply is complete ----
      console.log('assistant final reply:', accumulated); // debug

      // 1) Normalize duplicated "Related Files" sections
      const normalized = normalizeRelatedFilesSections(accumulated);

      // 2) Update the last assistant message with the normalized content
      setMessages((prev) => {
        const newMessages = [...prev];
        const lastIndex = newMessages.length - 1;
        if (lastIndex >= 0 && newMessages[lastIndex].role === 'assistant') {
          newMessages[lastIndex] = {
            ...newMessages[lastIndex],
            content: normalized,
          };
        }
        return newMessages;
      });

      // 3) Extract file links from normalized text
      const fileLinks = extractFileLinks(normalized);
      console.log('parsed fileLinks:', fileLinks); // debug

      setPreviewFiles(fileLinks);
      if (fileLinks.length > 0) {
        setShowFileButton(true); // shows bottom "📁 View Related Files" banner with timer
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🛑 Request aborted by user');
      } else {
        console.error('❌ Error during sendQuestion:', error);
        setMessages((prev) => [
          ...prev,
          {
            role: 'system',
            content: `❌ You are not authorized. ${error.message || 'Unknown error'}`,
            timestamp: new Date().toISOString(),
          },
        ]);
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  // ---- User submits normal question ----
  const handleSubmit = async (overridePage = 1, overrideSort = sortOrder) => {
    if (!question.trim() && sessionFiles.length === 0) return;

    // 📎 Prepend session file content if any
    let finalPrompt = question;
    if (sessionFiles.length > 0) {
      const fileContext = sessionFiles.map(f => `[Uploaded File: ${f.name}]\n${f.text}`).join('\n\n');
      finalPrompt = `${fileContext}\n\nUser Question: ${question}`;
    }

    const currentQuestion = question; // Display clean question to user in UI (optional, or show both)
    // Actually, to make it clear in UI, we should probably just send the prompt. 
    // But 'sendQuestion' adds to UI. Let's send the complex prompt to backend, but show simple q in UI?
    // For simplicity in this iteration: We will clear session files after sending so they don't stick forever.

    setQuestion('');
    setSessionFiles([]); // Clear after sending (one-off context) or keep? User said "match their documents", implies usage. 
    // "Upload... work on their doc". Ideally keep until cleared. 
    // User request: "current session only". I'll keep them until user removes or refreshes.
    // Actually, repeating full text every turn consumes tokens fast.
    // Better: Send it once, and let history catch it?
    // If I send it as "User: [File content]..." it enters history. So clearing from state is safer.

    // Let's go with: Send it as part of this turn.

    // We need to modify sendQuestion to accept "display text" vs "actual prompt" if we want to hide the huge text.
    // For now, let's just send the big prompt. It might look messy in chat bubble but it verifies what AI sees.
    // Wait, better UX: System message injection? No, user API interaction.

    // Refined approach:
    // We'll wrap sendQuestion to allow separate display/prompt, OR just dump it in.
    // Let's dump it in for transparency, but maybe truncate UI? 
    // I will stick to sending the modified prompt.

    await sendQuestion(finalPrompt, overridePage, overrideSort);
  };

  // 📎 Handle Temp Upload
  const handleSessionUpload = async (e) => {
    console.log('📂 File input changed');
    const file = e.target.files[0];
    if (!file) {
      console.log('❌ No file selected');
      return;
    }

    console.log(`📤 Uploading file: ${file.name}, Size: ${file.size}`);
    const toastId = localStorage.getItem('toastId') ? null : console.log('Uploading...');

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('🚀 Sending request to /chat/parse...');
      const res = await axiosInstance.post('/chat/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('✅ Upload success. Response:', res.data);
      if (res.data && res.data.text) {
        setSessionFiles(prev => [...prev, { name: res.data.filename, text: res.data.text }]);
      } else {
        console.warn('⚠️ Response missing text/filename:', res.data);
      }
    } catch (err) {
      console.error('❌ Session upload failed:', err);
      if (err.response) {
        console.error('Server Error Data:', err.response.data);
        console.error('Server Status:', err.response.status);
      }
      alert('Failed to upload file. Check console for details.');
    } finally {
      setLoading(false);
      e.target.value = null; // Reset input
    }
  };

  const removeSessionFile = (idx) => {
    setSessionFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // ---- Per-message "Show related files" button (always shown on bot messages) ----
  const handleShowRelatedFiles = async (assistantContent) => {
    const snippet =
      typeof assistantContent === 'string'
        ? assistantContent.slice(0, 400) // keep prompt short
        : '';

    const prompt = `Show related files for the following answer (you wrote this earlier in this conversation). Only return file references in markdown:\n\n${snippet}`;

    await sendQuestion(prompt, 1, sortOrder);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleClearChat = () => {
    localStorage.removeItem(localStorageKey);
    setMessages([
      {
        role: 'assistant',
        content: 'Hello, I am Your Departmental AI Chatbot. How can I help you today?',
        timestamp: new Date().toISOString(),
      },
    ]);
    setPage(1);
    setTotalPages(1);
    setSortOrder('desc');
  };

  const handlePrevPage = () => {
    if (page > 1 && !loading) {
      handleSubmit(page - 1, sortOrder);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages && !loading) {
      handleSubmit(page + 1, sortOrder);
    }
  };

  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSortOrder(newSort);
    if (!loading) {
      handleSubmit(1, newSort);
    }
  };

  // Group messages by turn (user + assistant)
  const groupedMessages = useMemo(() => {
    const groups = [];
    for (const message of messages) {
      if (message.role === 'user' || groups.length === 0) {
        groups.push([]);
      }
      groups[groups.length - 1].push({
        ...message,
        timestamp: message.timestamp || new Date().toISOString(),
      });
    }
    return groups;
  }, [messages]);

  return (
    <div className={styles.Chat}>
      <div className={styles.MessagesArea}>
        {groupedMessages.map((group, groupIndex) => (
          <div key={groupIndex} className={styles.Group}>
            {group.map((message, index) => {
              const { role, content, timestamp } = message;
              const messageKey = `${groupIndex}-${index}`;
              const isCopied = copiedIndex === messageKey;

              return (
                <div
                  key={index}
                  className={`${styles.Message} ${styles[role]}`}
                  data-role={role}
                >
                  <img src={getIcon(role)} alt={`${role} icon`} className={styles.avatar} />
                  <div className={styles.bubble}>
                    <div className={styles.senderName}>{getSenderName(role)}</div>

                    <div style={{ position: 'relative' }}>
                      {/* Welcome Hand Icon - Only for first msg */}
                      {role === 'assistant' && groupIndex === 0 && index === 0 && (
                        <div style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle', marginBottom: '2px' }}>
                          <MdWavingHand size={20} color="#fbbf24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                        </div>
                      )}
                      <Markdown
                        rehypePlugins={[rehypeRaw]}
                        components={{
                          a: ({ node, ...props }) => (
                            <a
                              {...props}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#46026c' }}
                            />
                          ),
                        }}
                      >
                        {typeof content === 'string' ? content : ''}
                      </Markdown>

                      {/* Actions row for assistant messages - Hide on first welcome message (index 0 of group 0) */}
                      {role === 'assistant' && (groupIndex > 0 || index > 0) && (
                        <div
                          style={{
                            marginTop: '10px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                            flexWrap: 'wrap',
                          }}
                        >
                          {/* Agile "Show related files" button */}
                          <button
                            onClick={() => handleShowRelatedFiles(content)}
                            aria-label="Show related files"
                            title="Show related files"
                            className={styles.agileFileButton}
                          >
                            <FaFileAlt />
                            <span>Sources</span>
                          </button>

                          {/* Copy button */}
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(content);
                              setCopiedIndex(messageKey);
                              setTimeout(() => setCopiedIndex(null), 1500);
                            }}
                            aria-label="Copy to clipboard"
                            title="Copy to clipboard"
                            style={{
                              backgroundColor: 'transparent',
                              color: isCopied ? '#22c55e' : 'var(--text-secondary)',
                              padding: '4px 8px',
                              fontSize: '0.75rem',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.backgroundColor = isCopied ? 'rgba(34, 197, 94, 0.1)' : 'rgba(0,0,0,0.05)')
                            }
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {isCopied ? (
                              <>
                                <LuCheck size={14} />
                                Copied
                              </>
                            ) : (
                              <>
                                <LuClipboardCopy size={14} />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    <span className={styles.timestamp}>
                      {new Date(timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {loading && (
          <div className={styles.loadingIndicator}>
            <span className={styles.loader}></span>
            <span>Thinking...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Global "View Related Files" banner with timer (latest answer that had files) */}
      {previewFiles.length > 0 && showFileButton && (
        <div style={{ position: 'relative', marginTop: '1rem', textAlign: 'center' }}>
          <button
            onClick={() => setShowFileModal(true)}
            style={{
              backgroundColor: '#5c159d',
              color: '#fff',
              padding: '0.6rem 2rem 0.6rem 2rem',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: '600',
              boxShadow: '0 4px 10px rgba(111, 11, 173, 0.1)',
              transition: 'opacity 0.5s ease, transform 0.5s ease',
              opacity: showFileButton ? 1 : 0,
              transform: showFileButton ? 'translateY(0)' : 'translateY(-10px)',
              position: 'relative',
              minWidth: '350px',
              display: 'inline-block',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4a107d')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#5c159d')}
            aria-label="View related files"
          >
            {/* Dismiss X */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowFileButton(false);
              }}
              aria-label="Dismiss related files"
              title="Dismiss"
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '1.6rem',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1,
              }}
            >
              <MdClose />
            </button>
            📁 View Related Files
            <span
              style={{
                position: 'absolute',
                top: '6px',
                right: '16px',
                backgroundColor: '#e11d48',
                color: 'white',
                borderRadius: '12px',
                padding: '2px 9px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              {countdown}s
            </span>
          </button>
        </div>
      )}

      {/* Modal – opens when bottom banner is clicked */}
      <FilePreviewModal
        isOpen={showFileModal}
        onClose={() => setShowFileModal(false)}
        files={previewFiles}
      />

      <div className={styles.InputArea} style={{ flexDirection: 'column', alignItems: 'stretch' }}>

        {/* Session Files Display (Moved Inside Stick Footer) */}
        {sessionFiles.length > 0 && (
          <div className="session-files" style={{ padding: '0 0 10px 10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {sessionFiles.map((file, idx) => (
              <div key={idx} style={{
                background: 'rgba(59, 130, 246, 0.9)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <span style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  📄 {file.name}
                </span>
                <button
                  onClick={() => removeSessionFile(idx)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                >
                  <MdClose size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className={styles.inputWrapper}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleSessionUpload}
            style={{ display: 'none' }}
            accept=".pdf,.docx,.txt,.md,.jpg,.png"
          />
          <button
            className={styles.attachButton}
            onClick={() => {
              console.log('📎 Paperclip clicked');
              fileInputRef.current?.click();
            }}
            title="Attach a file for this chat session only"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <MdAttachFile size={24} />
          </button>

          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
          />
          <button
            onClick={handleClearChat}
            className={styles.clearButton}
            title="Clear Conversation"
            aria-label="Clear Conversation"
          >
            <MdDeleteOutline size={22} />
          </button>

          {loading ? (
            <button onClick={handleStop} className={styles.stopButton} title="Stop Generating">
              <MdStop size={24} />
            </button>
          ) : (
            <button onClick={() => handleSubmit()} disabled={loading} className={styles.sendButton}>
              <IoSend size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatBox;
